import React, {
  DetailedHTMLProps,
  TextareaHTMLAttributes,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './Textarea.styles';
import { formatNumber, setSuffix } from '@/utils/helpers';
import { HelperText } from '@/components/UI/HelperText/HelperText';
import { useCombinedRefs } from '@/hooks/useCombinedRefs';
import { useAutosizeTextArea } from '@/hooks/useAutosizeTextArea';

interface TextareaProps
  extends DetailedHTMLProps<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  label?: string;
  withSymbolsCount?: boolean;
  helperText?: string;
  hasError?: boolean;
  height?: number;
  hasScrollbar?: boolean;
  maxSymbolsCount?: number;
  maxHeight?: number;
  minHeight?: number;
  isAutoSized?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      withSymbolsCount,
      helperText,
      hasError,
      height,
      hasScrollbar = true,
      maxSymbolsCount = 3000,
      maxHeight = 74,
      minHeight,
      isAutoSized,
      ...props
    },
    ref
  ) => {
    const { i18n, t } = useTranslation();
    const [maxSymbolsCountLocal, setMaxSymbolsCountLocal] =
      useState(maxSymbolsCount);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const combinedRef = useCombinedRefs<HTMLTextAreaElement | null>(
      ref,
      textAreaRef
    );
    useAutosizeTextArea(combinedRef.current, props.value, maxHeight);

    return (
      <div>
        {(label || withSymbolsCount) && (
          <S.TextareaHead>
            <S.TextareaLabel $variant="3">{label}</S.TextareaLabel>
            {withSymbolsCount && (
              <S.TextareaSymbolsCount $variant="1">
                {t(
                  setSuffix(
                    i18n,
                    maxSymbolsCountLocal.toString(),
                    'common.n_symbols'
                  ),
                  { n: formatNumber(maxSymbolsCountLocal) }
                )}
              </S.TextareaSymbolsCount>
            )}
          </S.TextareaHead>
        )}

        <S.TextareaField
          ref={isAutoSized ? combinedRef : ref}
          {...props}
          $height={height}
          $minHeight={minHeight}
          $hasScrollbar={hasScrollbar}
          onChange={event => {
            setMaxSymbolsCountLocal(
              maxSymbolsCount - event.target.value.length
            );
            props.onChange?.(event);
          }}
          maxLength={maxSymbolsCount}
        />

        {helperText && (
          <HelperText helperText={helperText} hasError={hasError} />
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
